import React from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import LeadCaptureForm from '@/components/common/LeadCaptureForm';

interface CTASectionProps {
  onFormSubmit: (data: Record<string, string>) => void;
}

const CTASection = ({ onFormSubmit }: CTASectionProps) => {
  const formFields = [
    {
      name: 'fullName',
      label: 'Full Name',
      type: 'text' as const,
      placeholder: 'John Doe',
      required: true,
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email' as const,
      placeholder: 'john@example.com',
      required: true,
    },
    {
      name: 'phone',
      label: 'Phone Number',
      type: 'tel' as const,
      placeholder: '(555) 123-4567',
      required: true,
    },
    {
      name: 'interest',
      label: 'Interested In',
      type: 'select' as const,
      required: true,
      options: [
        'Yellow Labrador Puppy',
        'Black Labrador Puppy',
        'Chocolate Labrador Puppy',
        'General Information',
      ],
    },
    {
      name: 'message',
      label: 'Message',
      type: 'textarea' as const,
      placeholder: 'Tell us about your family and what you\'re looking for...',
      required: false,
    },
  ];

  return (
    <section className="mb-16">
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="flex flex-col justify-center">
          <h2 className="mb-4 font-heading text-3xl font-bold text-foreground">
            Ready to Bring a Labrador Into Your Life?
          </h2>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            We'd absolutely love to help you find your perfect Labrador puppy! Reach out today to learn about our available puppies, how our breeding program works, and what the adoption process looks like. We're here to answer all your questions.
          </p>

          <div className="space-y-4">
            <Link
              href="/available-puppies"
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-5 shadow-warm transition-smooth hover:border-primary/30 hover:shadow-warm-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Icon name="PhotoIcon" size={24} variant="solid" className="text-primary" />
              </div>
              <div>
                <div className="font-heading text-lg font-semibold text-foreground">
                  View Available Puppies
                </div>
                <div className="text-sm text-muted-foreground">
                  See our current litters and upcoming puppies
                </div>
              </div>
              <Icon name="ChevronRightIcon" size={20} className="ml-auto text-muted-foreground" />
            </Link>

            <Link
              href="/health-guarantee"
              className="flex items-center gap-3 rounded-xl border border-border bg-card p-5 shadow-warm transition-smooth hover:border-primary/30 hover:shadow-warm-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-success/10">
                <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-success" />
              </div>
              <div>
                <div className="font-heading text-lg font-semibold text-foreground">
                  Our Health Guarantee
                </div>
                <div className="text-sm text-muted-foreground">
                  Learn about our comprehensive 2-year health protection
                </div>
              </div>
              <Icon name="ChevronRightIcon" size={20} className="ml-auto text-muted-foreground" />
            </Link>
          </div>
        </div>

        <div>
          <LeadCaptureForm
            fields={formFields}
            onSubmit={onFormSubmit}
            submitButtonText="Send Inquiry"
            title="Get In Touch"
            description="Fill out the form below and we'll respond within 24 hours"
          />
        </div>
      </div>
    </section>
  );
};

export default CTASection;